// txnHardDelete.js — OWNER-ONLY PERMANENT DELETE of one EOI / Deal row
// (owner decision 2026-11-02; backend: crm-aro-backend/lib/txnHardDelete.js).
//
// Three things live here, out of App.js (new code, new file):
//
//   dealLifecycleActions(row)   the Deals twin of eoiLifecycleActions — what a
//                               deal row may do. Today: edit / archive / cancel
//                               while live, and "delete" on every record-backed
//                               row. A lead-only legacy row (no record) cannot
//                               be deleted: there is nothing to delete.
//   canHardDeleteRow(access,row) the ONE render gate for the Delete control:
//                               the envelope's isOwner flag (never p.cu.role,
//                               never the viewer) AND a record-backed row.
//   TxnDeleteModal               the confirmation. The owner must TYPE the
//                               record number exactly; the button stays disabled
//                               until it matches. The server's refusal sentence
//                               (converted EOI / commission on record / demo) is
//                               shown in place, so a refused delete reads as a
//                               reason, not a crash.
//
// Nothing here reads the lead's agentId / status (R11): the row carries its own
// record identity (_rowKey, _recordNo, _fromRecord) and that is all this needs.

import React, { useState } from "react";

export var dealLifecycleActions = function (row) {
  if (!row) return [];
  var cancelled = row._txnStatus === "Cancelled" || row.dealStatus === "Deal Cancelled" || row.status === "Deal Cancelled";
  var base = cancelled ? ["edit", "archive"] : ["cancel", "edit", "archive"];
  return row._fromRecord ? base.concat(["delete"]) : base;
};

export var canHardDeleteRow = function (access, row) {
  return !!(access && access.isOwner === true && row && row._fromRecord && row._recordNo != null);
};

export var txnDeleteLabel = function (kind, recordNo) {
  return (kind === "deal" ? "Deal #" : "EOI #") + recordNo;
};

// props: kind ("eoi"|"deal"), row, onClose(), onConfirm() -> Promise (resolves
// when the server deleted it; rejects with the server's message otherwise),
// colors (the app's C palette, optional).
export function TxnDeleteModal(props) {
  var kind = props.kind, row = props.row || {};
  var recordNo = row._recordNo != null ? String(row._recordNo) : "";
  var label = txnDeleteLabel(kind, recordNo);
  var C = props.colors || { danger: "#DC2626", text: "#0F172A", textLight: "#64748B", border: "#E2E8F0" };
  var [typed, setTyped] = useState("");
  var [busy, setBusy]   = useState(false);
  var [error, setError] = useState("");
  var matches = typed.trim() === recordNo && recordNo !== "";
  var confirm = async function () {
    if (!matches || busy) return;
    setBusy(true); setError("");
    try { await props.onConfirm(); }
    catch (e) { setError((e && e.message) ? e.message : "Delete failed"); setBusy(false); }
  };
  var client = row.clientName || row.name || "";
  return (
    <div onClick={function (e) { if (e.target === e.currentTarget && !busy) props.onClose(); }}
         style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div role="dialog" aria-modal="true" aria-label={"Permanently delete " + label}
           style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 440, padding: 20, boxShadow: "0 20px 50px rgba(0,0,0,0.25)" }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: C.danger, marginBottom: 6 }}>Permanently delete {label}</div>
        <div style={{ fontSize: 13, color: C.text, lineHeight: 1.5, marginBottom: 10 }}>
          {client ? <span><b>{client}</b> — </span> : null}
          this removes the record, its files and its number for good. It is not a cancel: nothing will show on the Cancelled tab, and the number is never reused.
          The deletion is written to the audit log first.
        </div>
        <div style={{ fontSize: 12, color: C.textLight, marginBottom: 6 }}>Type the record number <b>{recordNo}</b> to confirm:</div>
        <input autoFocus value={typed} disabled={busy} onChange={function (e) { setTyped(e.target.value); }}
               onKeyDown={function (e) { if (e.key === "Enter") confirm(); if (e.key === "Escape" && !busy) props.onClose(); }}
               inputMode="numeric" placeholder={recordNo}
               style={{ width: "100%", boxSizing: "border-box", padding: "9px 10px", border: "1px solid " + (matches ? C.danger : C.border), borderRadius: 8, fontSize: 15, letterSpacing: 1 }} />
        {error ? <div style={{ marginTop: 10, padding: "8px 10px", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, color: "#991B1B", fontSize: 12, lineHeight: 1.4 }}>{error}</div> : null}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
          <button onClick={props.onClose} disabled={busy}
                  style={{ background: "transparent", border: "1px solid " + C.border, borderRadius: 8, padding: "7px 14px", cursor: busy ? "wait" : "pointer", fontSize: 13, color: C.text }}>Keep it</button>
          <button onClick={confirm} disabled={!matches || busy}
                  style={{ background: matches ? C.danger : "#CBD5E1", border: "none", borderRadius: 8, padding: "7px 14px", cursor: (!matches || busy) ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700, color: "#fff" }}>
            {busy ? "Deleting…" : "Delete " + label}
          </button>
        </div>
      </div>
    </div>
  );
}
